import { Injectable, computed, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { isCapacitorNative } from './capacitor-platform';

export type Platform = 'ios' | 'android' | 'web';

/**
 * Single source of truth for platform branching across the app. Resolves once
 * at boot and stamps body classes (`is-ios` / `is-android` / `is-mobile`) so
 * CSS variants fire without re-querying Capacitor. Components that need
 * TS-side branching inject this and read the signals; legacy callers may keep
 * using `isCapacitorNative()` unchanged for back-compat.
 */
@Injectable({ providedIn: 'root' })
export class PlatformService {
  private readonly _platform = signal<Platform>(this.detect());

  readonly platform = this._platform.asReadonly();
  readonly isIos = computed(() => this._platform() === 'ios');
  readonly isAndroid = computed(() => this._platform() === 'android');
  readonly isMobile = computed(() => this._platform() !== 'web');

  /** Stamp body classes once at app boot so CSS rules can match without JS. */
  applyBodyClasses(): void {
    if (typeof document === 'undefined') return;
    const cls = document.body.classList;
    cls.toggle('is-ios', this.isIos());
    cls.toggle('is-android', this.isAndroid());
    cls.toggle('is-mobile', this.isMobile());
    // Back-compat: keep existing rules that match `platform-native`.
    cls.toggle('platform-native', this.isMobile());
  }

  private detect(): Platform {
    if (!isCapacitorNative()) return 'web';
    try {
      const p = Capacitor.getPlatform();
      if (p === 'ios') return 'ios';
      if (p === 'android') return 'android';
    } catch {
      /* ignore */
    }
    // Capacitor without a recognised platform falls through as Android shell.
    return 'android';
  }
}
